'use client';

import React, { useState, useRef, useEffect } from 'react';

interface SelectProps {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
}

export const Select = ({ 
  value, 
  onValueChange, 
  children, 
  disabled = false
}: SelectProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleValueChange = (newValue: string) => {
    if (!disabled) {
      onValueChange(newValue);
      setIsOpen(false);
    }
  };

  // 從子組件中提取選項和顯示文本
  const extractOptions = () => {
    const options: { value: string; label: React.ReactNode }[] = [];
    React.Children.forEach(children, (child) => {
      if (React.isValidElement(child) && child.type === SelectItem) {
        const props = child.props as { value: string; children: React.ReactNode };
        options.push({
          value: props.value,
          label: props.children
        });
      }
    });
    return options;
  };

  const options = extractOptions();
  const selectedOption = options.find(option => option.value === value);

  return (
    <div ref={selectRef} className="relative">
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          if (child.type === SelectTrigger) {
            return React.cloneElement(child, {
              onClick: () => !disabled && setIsOpen(!isOpen),
              'aria-expanded': isOpen,
              disabled: disabled,
              selectedLabel: selectedOption ? 
                (typeof selectedOption.label === 'string' 
                  ? selectedOption.label 
                  : React.isValidElement(selectedOption.label) 
                    ? (selectedOption.label as React.ReactElement<{ children?: React.ReactNode }>).props.children
                    : selectedOption.label) 
                : undefined
            } as any);
          }
          
          if (child.type === SelectContent) {
            return React.cloneElement(child, {
              isOpen: isOpen,
              onValueChange: handleValueChange,
              onClose: () => setIsOpen(false),
              disabled: disabled,
              selectedValue: value
            } as any);
          }
        }
        return child;
      })}
    </div>
  );
};

interface SelectTriggerProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  disabled?: boolean;
}

export const SelectTrigger = React.forwardRef<HTMLButtonElement, SelectTriggerProps & { selectedLabel?: string }>(
  ({ 
    className = '', 
    children, 
    disabled = false, 
    selectedLabel,
    ...props 
  }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={`flex h-10 w-full items-center justify-between rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
        {...props}
      >
        <span 
          className={
            selectedLabel 
              ? "text-gray-900 dark:text-white font-medium" 
              : "text-gray-500 dark:text-gray-400"
          }
        >
          {selectedLabel || children}
        </span>
        <svg 
          className={`h-4 w-4 opacity-50 transition-transform ${disabled ? 'opacity-25' : ''}`}
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    );
  }
);

SelectTrigger.displayName = 'SelectTrigger';

interface SelectValueProps {
  placeholder?: string;
  value?: string;
}

export const SelectValue = ({ 
  placeholder = "請選擇", 
  value,
  children 
}: SelectValueProps & { children?: React.ReactNode }) => {
  // 優先使用 children，如果沒有則使用 value
  const displayText = children || value || placeholder;
  
  return (
    <span 
      className={
        displayText && displayText !== placeholder
          ? "text-gray-900 dark:text-white font-medium" 
          : "text-gray-500 dark:text-gray-400"
      }
    >
      {displayText}
    </span>
  );
};

interface SelectContentProps {
  children: React.ReactNode;
  className?: string;
  onValueChange?: (value: string) => void;
  onClose?: () => void;
  disabled?: boolean;
}

export const SelectContent = ({ 
  children, 
  className = '', 
  isOpen = false,
  onValueChange, 
  onClose, 
  disabled = false,
  selectedValue
}: SelectContentProps & { 
  isOpen?: boolean, 
  selectedValue?: string 
}) => {
  if (!isOpen) return null;

  return (
    <div className={`absolute z-50 min-w-[8rem] overflow-hidden rounded-md border bg-white dark:bg-gray-800 shadow-md top-full mt-1 ${className}`}>
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child) && child.type === SelectItem) {
          return React.cloneElement(child, {
            onSelect: (value: string) => {
              if (!disabled) {
                onValueChange?.(value);
                onClose?.();
              }
            },
            disabled: disabled,
            selected: (child.props as { value: string }).value === selectedValue
          } as any);
        }
        return child;
      })}
    </div>
  );
};

interface SelectItemProps {
  value: string;
  className?: string;
  onSelect?: (value: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
}

export const SelectItem = React.forwardRef<HTMLButtonElement, SelectItemProps & { selected?: boolean }>(
  ({ 
    value, 
    className = '', 
    children, 
    onSelect, 
    disabled = false,
    selected = false,
    ...props 
  }, ref) => {
    return (
      <button
        ref={ref}
        type="button"
        disabled={disabled}
        className={`relative flex w-full cursor-default select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-sm outline-none 
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-100 dark:hover:bg-gray-700 focus:bg-gray-100 dark:focus:bg-gray-700'}
          ${selected ? 'bg-gray-200 dark:bg-gray-600' : ''}
          ${className}`}
        onClick={() => !disabled && onSelect?.(value)}
        {...props}
      >
        {children}
        {selected && (
          <svg 
            className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-blue-600" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )}
      </button>
    );
  }
);

SelectItem.displayName = 'SelectItem';
